import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Card, Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UPLOAD_URL } from '../config';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PRODUCTS, SUBMIT_ORDER, CREATE_RAZORPAY_ORDER, VERIFY_RAZORPAY_PAYMENT } from '../graphql/operations';
import { useCart } from '../context/CartContext';
import UspStrip from '../components/UspStrip';
import Footer from '../components/Footer';
import OrderStepProgress from '../components/order/OrderStepProgress';
import ProductSelectionStep from '../components/order/ProductSelectionStep';
import ProductSpecsStep from '../components/order/ProductSpecsStep';
import SecureCheckoutStep from '../components/order/SecureCheckoutStep';
import OrderSummarySidebar from '../components/order/OrderSummarySidebar';
import OrderSuccessPanel from '../components/order/OrderSuccessPanel';
import AddedToCartModal from '../components/order/AddedToCartModal';
import SavedDesignsModal from '../components/order/SavedDesignsModal';
import { ContactInfo, OrderProduct } from '../components/order/OrderTypes';

// Local thumbnail images (keyed by productId)
import visiting from '../assets/Visiting-Cards.jpg';
import flex from '../assets/flex-printing.jpg';
import flyer from '../assets/Flyers-Brochures.jpg';
import stickers from '../assets/Stickers-Labels.jpg';
import wedding from '../assets/Wedding-Invitations.jpg';
import posters from '../assets/Posters.jpg';
import idcards from '../assets/ID-Cards.jpg';
import certificates from '../assets/Certificates.jpg';
import photoFrames from '../assets/photo-frames.png';
import backlightBoard from '../assets/boards/backlight.png';
import tBoard from '../assets/boards/tboard.png';
import aBoard from '../assets/boards/aboard.png';
import { handleNormalFlexPrice, handleStarFlexPrice } from '../utils/priceHelper';

const PRODUCT_IMAGES: Record<string, string> = {
  'visiting-cards': visiting,
  'flex-banners': flex,
  'flyers': flyer,
  'brochures': flyer,
  'stickers-labels': stickers,
  'wedding-invites': wedding,
  'posters': posters,
  'id-cards': idcards,
  'certificates': certificates,
  'photo-frames': photoFrames,
  'backlight-board': backlightBoard,
  't-board': tBoard,
  'a-board': aBoard,
};

const OrderPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal } = useCart();

  // Fetch products from GraphQL backend
  const { data: productsResult, loading: productsLoading } = useQuery(GET_PRODUCTS);
  const productsData: OrderProduct[] = React.useMemo(() => {
    return (productsResult?.products || []).map((p: any) => ({
      ...p,
      id: p.productId,
      img: PRODUCT_IMAGES[p.productId] || visiting,
    }));
  }, [productsResult]);

  // Mutations
  const [submitOrderMutation, { loading: orderSubmitting }] = useMutation(SUBMIT_ORDER);
  const [createRazorpayOrderMutation, { loading: creatingRzpOrder }] = useMutation(CREATE_RAZORPAY_ORDER);
  const [verifyPaymentMutation, { loading: verifyingPayment }] = useMutation(VERIFY_RAZORPAY_PAYMENT);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProductId, setSelectedProductId] = useState('visiting-cards');
  const [quantity, setQuantity] = useState(100);
  const [options, setOptions] = useState<Record<string, string | number>>({});

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: '',
    deliveryType: 'pickup',
    address: '',
    notes: '',
  });

  const [orderPrice, setOrderPrice] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  // File upload state
  const [designFile, setDesignFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null); // server filename
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);   // public URL

  const [backDesignFile, setBackDesignFile] = useState<File | null>(null);
  const [backUploadedFileName, setBackUploadedFileName] = useState<string | null>(null); // back server filename
  const [backUploadedFileUrl, setBackUploadedFileUrl] = useState<string | null>(null);   // back public URL

  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Cart addition success modal
  const [showAddedModal, setShowAddedModal] = useState(false);

  // Saved Designs Modal
  const [showSavedDesignsModal, setShowSavedDesignsModal] = useState(false);
  const [activeSideForModal, setActiveSideForModal] = useState<'front' | 'back'>('front');

  const handleSelectSavedDesign = (fileName: string, friendlyName: string) => {
    if (activeSideForModal === 'front') {
      setDesignFile(null);
      setUploadedFileName(fileName);
      setUploadedFileUrl(fileName);
      setUploadError('');
    } else {
      setBackDesignFile(null);
      setBackUploadedFileName(fileName);
      setBackUploadedFileUrl(fileName);
      setUploadError('');
    }
    setShowSavedDesignsModal(false);
  };

  const selectedProduct = React.useMemo(() => {
    return productsData.find(p => p.id === selectedProductId) || productsData[0];
  }, [productsData, selectedProductId]);

  // Route to Checkout directly if navigated from Cart Drawer or check auth
  useEffect(() => {
    if (location.state?.goToCheckout && cartItems.length > 0) {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login', { state: { from: '/order', fromState: { goToCheckout: true } } });
      } else {
        setCurrentStep(3);
      }
    }
  }, [location, cartItems, navigate]);

  // Check if a productId is passed as query param or state to pre-select it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productIdParam = params.get('productId') || location.state?.productId;
    if (productIdParam && productsData.some(p => p.id === productIdParam)) {
      setSelectedProductId(productIdParam);
    }
  }, [location.search, location.state, productsData]);

  // Guard step 3 (Checkout): redirect to login if not authenticated
  useEffect(() => {
    if (currentStep === 3) {
      const token = localStorage.getItem('token');
      if (!token) {
        setCurrentStep(1); // Revert to safe default first
        navigate('/login', { state: { from: '/order', fromState: { goToCheckout: true } } });
      } else {
        const storedUsername = localStorage.getItem('username') || '';
        const storedShippingAddress = localStorage.getItem('shippingAddress') || '';
        setContactInfo(prev => ({
          ...prev,
          name: prev.name || storedUsername,
          address: prev.address || storedShippingAddress,
        }));
      }
    }
  }, [currentStep, navigate]);

  // Initialize options for selected product
  useEffect(() => {
    if (!selectedProduct) return;
    const defaultOptions: Record<string, string | number> = {};
    selectedProduct.options.forEach(opt => {
      if (opt.type === 'number') {
        defaultOptions[opt.name] = opt.min || 1;
      } else if (opt.choices && opt.choices.length > 0) {
        defaultOptions[opt.name] = opt.choices[0].label;
      }
    });
    setOptions(defaultOptions);

    // Set reasonable default quantities per category
    if (selectedProduct.id === 'visiting-cards') setQuantity(100);
    else if (selectedProduct.id === 'flex-banners') setQuantity(1);
    else if (selectedProduct.id === 'flyers' || selectedProduct.id === 'brochures') setQuantity(250);
    else if (selectedProduct.id === 'stickers-labels') setQuantity(100);
    else if (selectedProduct.id === 'wedding-invites') setQuantity(100);
    else if (selectedProduct.id === 'posters') setQuantity(5);
    else if (selectedProduct.id === 'id-cards') setQuantity(5);
    else if (selectedProduct.id === 'certificates') setQuantity(20);
    else if (selectedProduct.id === 'photo-frames') setQuantity(1);
  }, [selectedProductId, selectedProduct]);

  // Recalculate price
  useEffect(() => {
    if (!selectedProduct) return;
    let pricePerUnit = selectedProduct.basePrice;
    if (selectedProduct.id === 'flex-banners') {
      let extraOptionsPrice = 0;
      let basePriceForFlex = pricePerUnit;
      let totalPriceForFlex = 0;
      const w = Number(options['width']) || 3;
      const h = Number(options['height']) || 2;
      const totalSquareFeet = w * h;
      selectedProduct.options.forEach((opt: any) => {
        const selectedValue = options[opt.name];
        if (opt.type === 'select') {
          const choice = opt.choices?.find((c: any) => c.label === selectedValue);

          if (selectedValue === 'Standard Flex (Economical)') {
            if (totalSquareFeet >= 100) {
              if (choice) basePriceForFlex = choice.extraCost || 0;
              totalPriceForFlex = totalSquareFeet * basePriceForFlex;
            } else {
              totalPriceForFlex = handleNormalFlexPrice(totalSquareFeet);
            }
          }

          if (selectedValue === 'Star Flex (Thick & Premium)') {
            if (totalSquareFeet >= 31) {
              if (choice) basePriceForFlex = choice.extraCost || 0;
              totalPriceForFlex = totalSquareFeet * basePriceForFlex;
            } else {
              totalPriceForFlex = handleStarFlexPrice(totalSquareFeet);
            }
          }

          if (selectedValue === 'Vinyl Printing') {
            basePriceForFlex = 75;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          }

          if (selectedValue === 'One Way Vision Printing') {
            basePriceForFlex = 85;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          }

          if (selectedValue === 'Backlit Flex (For Lightboxes)') {
            basePriceForFlex = 200;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          }

          if (selectedValue === 'Oil Posters') {
            basePriceForFlex = 200;
            totalPriceForFlex = totalSquareFeet * basePriceForFlex;
          }

        } else if (opt.type === 'radio') {
          const choice = opt.choices?.find((c: any) => c.label === selectedValue);
          if (choice) extraOptionsPrice = choice.extraCost || 0;
        }
      });
      const overallPrixeForSelectedQunatity = totalPriceForFlex * quantity;
      const extraPriceForQuantity = extraOptionsPrice * quantity;
      setOrderPrice(Math.round(overallPrixeForSelectedQunatity + extraPriceForQuantity));
    } else {
      // Apply adjustments for selected options
      selectedProduct.options.forEach(opt => {
        const selectedValue = options[opt.name];
        if (opt.type === 'select' || opt.type === 'radio') {
          const choice = opt.choices?.find(c => c.label === selectedValue);
          if (choice) {
            pricePerUnit += choice.extraCost;
          }
        }
      });

      let total = 0;
      if (selectedProduct.id === 'flex-banners') {
        const w = Number(options['width']) || 3;
        const h = Number(options['height']) || 2;
        const area = w * h;
        total = pricePerUnit * area * quantity;
      } else {
        total = pricePerUnit * quantity;
      }

      // Round to nearest integer for INR neatness
      setOrderPrice(Math.round(total));
    }
  }, [selectedProduct, options, quantity]);

  const handleProductChange = (id: string) => {
    setSelectedProductId(id);
  };

  const handleOptionChange = (name: string, value: string | number) => {
    setOptions(prev => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactInfo(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (side === 'front') {
      setDesignFile(file);
      setUploadedFileName(null);
      setUploadedFileUrl(null);
    } else {
      setBackDesignFile(file);
      setBackUploadedFileName(null);
      setBackUploadedFileUrl(null);
    }
    setUploadError('');
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('designFile', file);

    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', UPLOAD_URL);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const result = JSON.parse(xhr.responseText);
            if (result.success) {
              if (side === 'front') {
                setUploadedFileName(result.filename);
                setUploadedFileUrl(result.url);
              } else {
                setBackUploadedFileName(result.filename);
                setBackUploadedFileUrl(result.url);
              }
              resolve();
            } else {
              reject(new Error(result.message || 'Upload failed.'));
            }
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.message || `Server error ${xhr.status}`));
            } catch {
              reject(new Error(`Server error ${xhr.status}`));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload.'));
        xhr.send(formData);
      });
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed.');
      if (side === 'front') {
        setDesignFile(null);
      } else {
        setBackDesignFile(null);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = async (side: 'front' | 'back') => {
    const fileName = side === 'front' ? uploadedFileName : backUploadedFileName;
    if (fileName) {
      try {
        await fetch(`${UPLOAD_URL}/${fileName}`, { method: 'DELETE' });
      } catch (_) {
        // non-critical cleanup
      }
    }
    if (side === 'front') {
      setDesignFile(null);
      setUploadedFileName(null);
      setUploadedFileUrl(null);
    } else {
      setBackDesignFile(null);
      setBackUploadedFileName(null);
      setBackUploadedFileUrl(null);
    }
    setUploadError('');
    setUploadProgress(0);
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    if (step === 2) {
      if (selectedProduct.id === 'flex-banners') {
        const w = Number(options['width']);
        const h = Number(options['height']);
        if (!w || w <= 0) newErrors.width = 'Width must be greater than 0';
        if (!h || h <= 0) newErrors.height = 'Height must be greater than 0';
      }
      if (!quantity || quantity <= 0) {
        newErrors.quantity = 'Quantity must be positive';
      }
    }
    if (step === 3) {
      if (cartItems.length === 0) {
        newErrors.cart = 'Your cart is empty. Please add items to your cart.';
      }
      if (!contactInfo.name.trim()) newErrors.name = 'Name is required';
      if (!contactInfo.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(contactInfo.phone.replace(/[\s-+]/g, '').slice(-10))) {
        newErrors.phone = 'Please enter a valid 10-digit number';
      }
      if (!contactInfo.email.trim()) {
        newErrors.email = 'Email address is required';
      } else if (!/\S+@\S+\.\S+/.test(contactInfo.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (contactInfo.deliveryType === 'home' && !contactInfo.address.trim()) {
        newErrors.address = 'Delivery address is required for home shipping';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add Item to Cart
  const handleAddToCart = () => {
    if (!validateStep(2)) return;

    const isDoubleSided = Object.values(options).some(
      val => String(val).toLowerCase().includes('double sided') || String(val).toLowerCase() === 'double sided'
    );

    const designFileName = isDoubleSided
      ? (uploadedFileName || backUploadedFileName
        ? JSON.stringify({ front: uploadedFileName || null, back: backUploadedFileName || null })
        : null)
      : (uploadedFileName || null);

    const finalUploadedFileUrl = isDoubleSided
      ? (uploadedFileUrl || backUploadedFileUrl
        ? JSON.stringify({ front: uploadedFileUrl || null, back: backUploadedFileUrl || null })
        : null)
      : (uploadedFileUrl || null);

    addToCart({
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      quantity,
      options,
      estimatedPrice: orderPrice,
      designFileName,
      uploadedFileUrl: finalUploadedFileUrl,
    });

    setShowAddedModal(true);
  };

  const resetConfigFields = () => {
    setDesignFile(null);
    setUploadedFileName(null);
    setUploadedFileUrl(null);
    setBackDesignFile(null);
    setBackUploadedFileName(null);
    setBackUploadedFileUrl(null);
    setUploadError('');
    setUploadProgress(0);
    setIsUploading(false);

    // reset options
    const defaultOptions: Record<string, string | number> = {};
    selectedProduct.options.forEach(opt => {
      if (opt.type === 'number') {
        defaultOptions[opt.name] = opt.min || 1;
      } else if (opt.choices && opt.choices.length > 0) {
        defaultOptions[opt.name] = opt.choices[0].label;
      }
    });
    setOptions(defaultOptions);

    setCurrentStep(1);
    setShowAddedModal(false);
  };

  // Dynamically load Razorpay SDK script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Submit order & Trigger Razorpay
  const handleCheckoutAndPay = async () => {
    if (!validateStep(3)) return;
    setSubmitError('');

    // Load Razorpay checkout script
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      setSubmitError('Razorpay SDK failed to load. Please check your internet connection.');
      return;
    }

    try {
      // 1. Submit the order to local database (status: pending, paymentStatus: pending)
      const submitItems = cartItems.map(item => ({
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        options: Object.entries(item.options).map(([k, v]) => ({ key: k, value: String(v) })),
        designFileName: item.designFileName,
        estimatedPrice: item.estimatedPrice,
      }));

      const { data: orderData } = await submitOrderMutation({
        variables: {
          input: {
            items: submitItems,
            totalAmount: cartTotal,
            customerName: contactInfo.name,
            customerEmail: contactInfo.email,
            customerPhone: contactInfo.phone,
            deliveryType: contactInfo.deliveryType,
            deliveryAddress: contactInfo.address || '',
            notes: contactInfo.notes || '',
          },
        },
      });

      if (!orderData?.submitOrder?.success) {
        setSubmitError(orderData?.submitOrder?.message || 'Failed to initialize order details.');
        return;
      }

      // const pdpOrderId = orderData.submitOrder.orderId;

      // // 2. Create the Razorpay Order via Backend mutation
      // const { data: rzpData } = await createRazorpayOrderMutation({
      //   variables: { amount: cartTotal },
      // });

      // if (!rzpData?.createRazorpayOrder?.id) {
      //   setSubmitError('Failed to initialize transaction with payment gateway.');
      //   return;
      // }

      // const rzpOrder = rzpData.createRazorpayOrder;

      // // 3. Open Razorpay Modal
      // const options = {
      //   key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_placeholder_key',
      //   amount: rzpOrder.amount * 100, // in paise
      //   currency: rzpOrder.currency,
      //   name: 'Prathitha Digital Printers',
      //   description: 'Payment for order ' + pdpOrderId,
      //   order_id: rzpOrder.id,
      //   prefill: {
      //     name: contactInfo.name,
      //     email: contactInfo.email,
      //     contact: contactInfo.phone,
      //   },
      //   theme: {
      //     color: '#e91e8c', // Pink accent
      //   },
      //   handler: async function (response: any) {
      //     // 4. Verify payment signature on backend
      //     try {
      //       const { data: verifyData } = await verifyPaymentMutation({
      //         variables: {
      //           orderId: response.razorpay_order_id,
      //           paymentId: response.razorpay_payment_id,
      //           signature: response.razorpay_signature,
      //           pdpOrderId: pdpOrderId,
      //         },
      //       });

      //       if (verifyData?.verifyRazorpayPayment?.success) {
      //         setOrderId(pdpOrderId);
      //         setIsSubmitted(true);
      //         clearCart();
      //         window.scrollTo({ top: 0, behavior: 'smooth' });
      //       } else {
      //         setSubmitError(verifyData?.verifyRazorpayPayment?.message || 'Signature verification failed.');
      //       }
      //     } catch (verifyErr: any) {
      //       setSubmitError('Verification error: ' + verifyErr.message);
      //     }
      //   },
      //   modal: {
      //     ondismiss: function () {
      //       setSubmitError('Payment cancelled by user. Order is saved as pending.');
      //     },
      //   },
      // };

      // const rzp = new (window as any).Razorpay(options);
      // rzp.open();
    } catch (err: any) {
      console.error(err);
      setSubmitError(err.message || 'Checkout failed. Please try again.');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetOrderFlow = () => {
    setCurrentStep(1);
    setSelectedProductId('visiting-cards');
    setQuantity(100);
    setDesignFile(null);
    setUploadedFileName(null);
    setUploadedFileUrl(null);
    setBackDesignFile(null);
    setBackUploadedFileName(null);
    setBackUploadedFileUrl(null);
    setUploadError('');
    setUploadProgress(0);
    setIsUploading(false);
    setContactInfo({
      name: '',
      email: '',
      phone: '',
      deliveryType: 'pickup',
      address: '',
      notes: '',
    });
    setIsSubmitted(false);
    setOrderId('');
    setErrors({});
    setSubmitError('');
  };

  const getWhatsAppLink = () => {
    const itemsSummary = cartItems
      .map(item => `• *${item.productName}* (${item.quantity} units) - ₹${item.estimatedPrice.toLocaleString('en-IN')}`)
      .join('\n');

    const msg = `Hi Prathitha Digital Printers! 👋
I would like to confirm my print order payment:

*Order Reference:* ${orderId}
*Total Paid:* ₹${cartTotal.toLocaleString('en-IN')}

*Items List:*
${itemsSummary}

*Customer Name:* ${contactInfo.name}
*Contact Phone:* ${contactInfo.phone}
*Delivery Method:* ${contactInfo.deliveryType === 'pickup' ? 'Store Pickup' : 'Home Delivery'}
${contactInfo.deliveryType === 'home' ? `*Delivery Address:* ${contactInfo.address}\n` : ''}${contactInfo.notes ? `*Notes:* ${contactInfo.notes}\n` : ''}
Thank you!`;

    return `https://wa.me/919150808130?text=${encodeURIComponent(msg)}`;
  };

  if (productsLoading || !selectedProduct) {
    return (
      <div className="page-fade d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 68px)' }}>
        <Spinner animation="border" className="mb-3 text-primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="text-muted fw-semibold mt-2">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="page-fade d-flex flex-column order-page" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div style={{ flex: 1 }}>
        <div className="breadcrumb-bar">
          <Link to="/">Home</Link> &gt; Order Online
        </div>

        {/* Page Hero */}
        <div className="order-hero text-center py-5" style={{ background: 'linear-gradient(135deg, #fff5fb 0%, #fdf2f8 50%, #ffffff 100%)' }}>
          <Container>
            <h2 className="display-5 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>
              Order <span style={{ background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Online</span>
            </h2>
            <div style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', borderRadius: '2px', margin: '0 auto 16px' }}></div>
            <p className="lead text-muted mx-auto mb-4" style={{ maxWidth: '680px', fontSize: '16px' }}>
              Configure your print job, add customized items to your cart, and make secure instant payments using Razorpay.
            </p>
          </Container>
        </div>

        {/* Step Indicator */}
        {!isSubmitted && (
          <Container className="my-4">
            <OrderStepProgress currentStep={currentStep} />
          </Container>
        )}

        {/* Core Layout */}
        <Container className="pb-5">
          {isSubmitted ? (
            <OrderSuccessPanel
              orderId={orderId}
              contactInfo={contactInfo}
              cartTotal={cartTotal}
              getWhatsAppLink={getWhatsAppLink}
              onResetOrderFlow={resetOrderFlow}
            />
          ) : (
            /* ORDER PROCESS FORM */
            <Row className="g-4 mt-2">
              {/* Form Side */}
              <Col lg={currentStep === 3 ? 12 : 8}>
                <div className="bg-white rounded-4 shadow-sm border border-light p-4" style={{
                  minHeight: '480px',
                  borderRadius: '14px',
                  border: '1px solid #eaeef5',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                }}>

                  {/* STEP 1: SELECT CATEGORY */}
                  {currentStep === 1 && (
                    <ProductSelectionStep
                      productsData={productsData}
                      selectedProductId={selectedProductId}
                      onProductChange={handleProductChange}
                      onNext={() => {
                        setCurrentStep(2);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      loading={productsLoading}
                    />
                  )}

                  {/* STEP 2: SPECIFICATIONS */}
                  {currentStep === 2 && selectedProduct && (
                    <ProductSpecsStep
                      selectedProduct={selectedProduct}
                      options={options}
                      errors={errors}
                      quantity={quantity}
                      onOptionChange={handleOptionChange}
                      onQuantityChange={setQuantity}
                      onFileChange={handleFileChange}
                      designFile={designFile}
                      uploadedFileName={uploadedFileName}
                      backDesignFile={backDesignFile}
                      backUploadedFileName={backUploadedFileName}
                      onRemoveFile={removeFile}
                      uploadError={uploadError}
                      isUploading={isUploading}
                      uploadProgress={uploadProgress}
                      onAddToCart={handleAddToCart}
                      onBack={prevStep}
                      orderPrice={orderPrice}
                      onOpenSavedDesigns={(side) => {
                        setActiveSideForModal(side);
                        setShowSavedDesignsModal(true);
                      }}
                    />
                  )}

                  {currentStep === 3 && (
                    <SecureCheckoutStep
                      cartItems={cartItems}
                      cartTotal={cartTotal}
                      contactInfo={contactInfo}
                      errors={errors}
                      submitError={submitError}
                      orderSubmitting={orderSubmitting}
                      creatingRzpOrder={creatingRzpOrder}
                      verifyingPayment={verifyingPayment}
                      onContactChange={handleContactChange}
                      onDeliveryTypeChange={(deliveryType) => setContactInfo(prev => ({ ...prev, deliveryType }))}
                      onRemoveFromCart={removeFromCart}
                      onUpdateQuantity={updateQuantity}
                      onBack={() => setCurrentStep(1)}
                      onCheckout={handleCheckoutAndPay}
                    />
                  )}

                </div>
              </Col>

              {/* Sidebar Order Summary */}
              {currentStep !== 3 && selectedProduct && (
                <Col lg={4}>
                  <OrderSummarySidebar
                    selectedProduct={selectedProduct}
                    options={options}
                    quantity={quantity}
                    orderPrice={orderPrice}
                  />
                </Col>
              )}
            </Row>
          )}
        </Container>

      </div>

      <AddedToCartModal
        show={showAddedModal}
        onHide={resetConfigFields}
        productName={selectedProduct?.name || ''}
        orderPrice={orderPrice}
        onCheckoutNow={() => {
          setShowAddedModal(false);
          setCurrentStep(3);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <SavedDesignsModal 
        show={showSavedDesignsModal}
        onHide={() => setShowSavedDesignsModal(false)}
        onSelectDesign={handleSelectSavedDesign}
      />

      <UspStrip />
      <Footer />
    </div>
  );
};

export default OrderPage;
